// @file Surface post filter store
import { getVuexStore } from 'bits/pinia'
import { SurfaceFilter } from 'enums'
import { intersectionBy } from 'lodash'
import { defineStore } from 'pinia'
import type { Post } from 'types'
import { computed, ref } from 'vue'
import type { RootState } from 'vuexstore/surface/types'

interface PostStatusFilter {
  group: 'post_status'
  value: 'published' | 'submitted'
}

interface PostColorFilter {
  group: 'post_color'
  value: 'default' | 'red' | 'orange' | 'green' | 'blue' | 'purple'
}

interface PostAuthorFilter {
  group: 'post_author'
  value: string // can be username, email, or user_id
}

type PostFilter = PostStatusFilter | PostColorFilter | PostAuthorFilter

type GetPostFilterTypeForGroup<group> = group extends 'post_status'
  ? PostStatusFilter
  : group extends 'post_color'
  ? PostColorFilter
  : group extends 'post_author'
  ? PostAuthorFilter
  : undefined

/**
 * Convenient, type-aware function that returns an array of filters for a given filter group.
 */
function getFiltersForGroup<G extends PostFilter['group']>(
  filters: PostFilter[],
  group: G,
): Array<GetPostFilterTypeForGroup<G>> {
  return filters.filter((f) => f.group === group) as Array<GetPostFilterTypeForGroup<G>>
}

/**
 * Returns posts that match the status filters (OR logic).
 */
function filterPostsByStatus(posts: Post[], filters: PostFilter[]): Post[] {
  const uniqueFilters = getFiltersForGroup(filters, 'post_status')
  const results: Post[] = []
  uniqueFilters.forEach((f) => {
    results.push(...posts.filter((p) => (f.value === 'published' ? p.published === true : p.published !== true)))
  })
  return results.length === 0 ? posts : results
}

/**
 * Returns posts that match the color filters (OR logic).
 */
function filterPostsByColor(posts: Post[], filters: PostFilter[]): Post[] {
  const uniqueFilters = getFiltersForGroup(filters, 'post_color')
  const results: Post[] = []
  uniqueFilters.forEach((f) => {
    results.push(...posts.filter((p) => p.color === f.value || (p.color == null && f.value === 'default')))
  })
  return results.length === 0 ? posts : results
}

/**
 * Returns posts that match the author filters (OR logic).
 * Authors are matched by looking up the username first, then the email, then the user_id.
 */
function filterPostsByAuthor(posts: Post[], filters: PostFilter[]): Post[] {
  const uniqueFilters = getFiltersForGroup(filters, 'post_author')
  const results: Post[] = []
  uniqueFilters.forEach((f) => {
    results.push(
      ...posts.filter(
        (p) => p.author?.username === f.value || p.author?.email === f.value || p.author?.id?.toString() === f.value,
      ),
    )
  })
  return results.length === 0 ? posts : results
}

/**
 * Returns posts that match all set of filters by groups (AND logic).
 */
export function filterPosts(posts: Post[], filters: PostFilter[]): Post[] {
  if (filters.length === 0) return posts
  return intersectionBy(
    filterPostsByStatus(posts, filters),
    filterPostsByColor(posts, filters),
    filterPostsByAuthor(posts, filters),
    'cid',
  )
}

export const useSurfaceFilterStore = defineStore('surfaceFilter', () => {
  const surfaceVuexStore = getVuexStore<RootState>()

  // Legacy filter value from Vuex store. Won't use this in the new search & filter feature.
  const filter = computed<SurfaceFilter>(() => surfaceVuexStore?.getters['postFilter/filter'])

  // State
  const xFilterModal = ref(false)
  const filters = ref<PostFilter[]>([])

  return {
    // Legacy
    filter,

    // State
    xFilterModal,
    filters,
  }
})
